import qs from "query-string";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import AlgoliaProvider from "@repo/shopifyV2/algolia/AlgoliaProvider";
import { trackAlgoliaClickOn } from "@repo/shopifyV2/algolia/insights";
import { useIsAlgoliaDown } from "@repo/shopifyV2/algolia/status";
import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import type { IProductDetailQuery } from "@repo/shopifyV2/interface/product";
import type { ISearchEdge, ISearchQuery } from "@repo/shopifyV2/interface/search";
import type { Product, ProductVariantConnection } from "@repo/shopifyV2/types/storefront.types";
import { getTotalProductCount } from "@repo/shopifyV2/utils/collections";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { SEARCH_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useAlgoliaSearchResult from "@hooks/algolia/useAlgoliaSearchResult";
import useHeaderScroll from "@hooks/useHeaderScroll";

import { ALGOLIA_ENABLED } from "@lib/algolia";

import useSearch from "@queries/useSearch";

import { useLayoutStore } from "@store/layout";

interface ISearchResultViewProps {
  queryParameter: string;
  searchData: ISearchQuery["search"] | undefined;
  products: ISearchEdge[];
  expandProducts: ISearchEdge[];
  hasNextPage: boolean;
  loading: boolean;
  onFetchMore: () => unknown;
}

/** 검색 결과 화면. 데이터 출처(Shopify / Algolia)와 무관하게 같은 화면을 그린다. */
function SearchResultView({
  queryParameter,
  searchData,
  products,
  expandProducts,
  hasNextPage,
  loading,
  onFetchMore,
}: ISearchResultViewProps) {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { isPC } = useCheckDevice();

  useHeaderScroll();

  const queryString = useMemo(() => qs.parse(search), [search]);

  const { showFooter, setMeta, clearMeta, openSearch } = useLayoutStore();

  const [showFilter, setShowFilter] = useState<boolean>(false);

  useEffect(() => {
    if (!queryParameter) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openSearch(true);
      }, 500);
    }

    showFooter(true);
  }, []);

  // 검색어가 바뀌면 필터 패널을 닫고 맨 위로 올린다
  useEffect(() => {
    setShowFilter(false);
    window.scrollTo(0, 0);
  }, [queryParameter]);

  useEffect(() => {
    setMeta({ title: `Search Result for "${queryParameter}" - ${DEFAULT_TITLE}` });

    return () => {
      clearMeta();
    };
  }, [queryParameter]);

  const showProductList = useMemo(
    () => (queryString.view === "combined" ? products : expandProducts),
    [queryString, expandProducts, products],
  );

  const totalProducts = useMemo(() => getTotalProductCount(searchData?.productFilters || []), [searchData]);

  return (
    <>
      {!searchData && loading ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <>
              <section className="pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 sticky top-30 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-46">
                {showFilter ? (
                  <Filter
                    filters={searchData?.productFilters || []}
                    onClickClose={() => {
                      setShowFilter(false);
                    }}
                  />
                ) : (
                  <div className="flex flex-col gap-10">
                    <Button
                      as="text"
                      className="justify-end"
                      onClick={() => {
                        setShowFilter(!showFilter);
                      }}
                    >
                      {`Filter & Sort (${showProductList.length > 0 ? totalProducts : 0})`}
                    </Button>
                  </div>
                )}
              </section>
              <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <div
                          className="contents"
                          key={`${node.id}_${index}`}
                          onClickCapture={() => {
                            trackAlgoliaClickOn(node);
                          }}
                        >
                          <ProductCard
                            color={node.options.find((item) => item.name === "Color")?.values || []}
                            releaseDate={node?.release_date}
                            tags={node.tags}
                            title={node.title}
                            variants={node.variants}
                          />
                        </div>
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:col-span-4 pc:gap-2.5 col-span-8 flex flex-col gap-2">
                        <div className="flex flex-col gap-1 leading-none">
                          {isPC ? <h1>{`Search Result for "${queryParameter}"`}</h1> : null}
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      onFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="pc:col-span-12 col-span-8 mt-10 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={searchData?.productFilters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <>
                  {showProductList.length > 0 && (
                    <div className="sticky top-0 z-20 col-span-8 flex w-full justify-between gap-1 self-start bg-white py-2.5" id="filter">
                      <h1 className="text-base! leading-none">{`Search Result for "${queryParameter}"`}</h1>
                      <Button
                        as="wrapper"
                        onClick={() => {
                          setShowFilter(true);
                        }}
                      >
                        <Icon name="Filter" />
                      </Button>
                    </div>
                  )}
                </>
              )}

              <section className="col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <div
                          className="contents"
                          key={`${node.id}_${index}`}
                          onClickCapture={() => {
                            trackAlgoliaClickOn(node);
                          }}
                        >
                          <ProductCard
                            color={node.options.find((item) => item.name === "Color")?.values || []}
                            releaseDate={node?.release_date}
                            tags={node.tags}
                            title={node.title}
                            variants={node.variants}
                          />
                        </div>
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:gap-2.5 col-span-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-1 leading-none">
                          <h1>{`Search Result for "${queryParameter}"`}</h1>
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      onFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="col-span-8 mt-10 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </>
      )}
    </>
  );
}

/** 기존 Shopify Storefront 검색. Algolia 를 쓰지 않는 환경과 Algolia 장애 시 사용한다. */
function ShopifySearchResult() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [searchParams] = useSearchParams();

  const queryParameter = searchParams.get("q");

  const queryString = useMemo(() => qs.parse(search), [search]);

  const [searchData, setSearchData] = useState<ISearchQuery["search"] | undefined>();
  const [products, setProducts] = useState<ISearchEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ISearchEdge[]>([]);

  // 검색어 변경 될때 초기화 해줘야 함
  useEffect(() => {
    setExpandProducts([]);
    setSearchData(undefined);
  }, [queryParameter]);

  const variables = useMemo(() => {
    const sortQueries = SEARCH_SORT.find((item) => item.name === queryString.sort);

    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) => (key === "category" ? { productType: item } : { variantOption: { name: key, value: item } }));
      })
      .flat();

    return {
      first: queryString.view === "seperate" ? 12 : 24, // 쿼리 1회에 조회하는 product 수
      types: "PRODUCT",
      query: queryParameter,
      sortKey: sortQueries?.value ?? "RELEVANCE",
      reverse: sortQueries?.reverse ?? true,
      prefix: "LAST",
      productFilters: filterQueries || [],
    };
  }, [queryParameter, queryString]);

  const { fetchSearch, searchData: queryData, searchLoading: loading, fetchMoreSearch: fetchMore } = useSearch(variables);

  // 최초 조회 + 검색어/필터 변경 시 재조회
  useEffect(() => {
    void fetchSearch(variables);
  }, [variables]);

  // 조회/추가 로드 완료 시 목록 상태 갱신 (apollo onCompleted 대응). 실패 토스트는 useSearch 가 띄운다
  useEffect(() => {
    if (!queryData) return;

    if (!queryData.search) {
      navigate(URLS.NOT_FOUND);
      return;
    }

    // 페이지 병합 과정에서 같은 상품이 중복될 수 있어 id 기준으로 한 번 걸러낸다
    const seen = new Set<string>();
    const searchList =
      (queryData.search.edges as ISearchEdge[])
        ?.filter(({ node }) => Boolean(!(node as IProductDetailQuery["product"])?.vendor.includes("GWP")))
        ?.filter(({ node }) => (node as IProductDetailQuery["product"])?.hidden?.value !== "true")
        ?.filter(({ node }) => {
          const id = (node as ISearchEdge["node"])?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        }) || [];

    setSearchData(queryData.search);
    setProducts(searchList);
    setExpandProducts(getExpandList(searchList));
  }, [queryData]);

  const getExpandList = (searchList: ISearchEdge[]) => {
    const expandList: ISearchEdge[] = [];

    searchList.forEach(({ node }) => {
      const colorOptions = (node as Product).options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = (node as Product).variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ISearchEdge = {
            node: {
              ...node,
              options: (node as Product).options.map((option) => (option.name === "Color" ? { ...option, values: [color] } : option)),
              variants: { edges: colorList } as ProductVariantConnection,
            },
          } as ISearchEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  const hasNextPage = useMemo(() => searchData?.pageInfo.hasNextPage || false, [searchData?.pageInfo]);
  const handleFetchMore = async () => {
    await fetchMore();
  };

  return (
    <SearchResultView
      expandProducts={expandProducts}
      hasNextPage={hasNextPage}
      loading={loading}
      onFetchMore={handleFetchMore}
      products={products}
      queryParameter={queryParameter ?? ""}
      searchData={searchData}
    />
  );
}

/** Algolia 검색. 순위 · 필터 · 카드 데이터 모두 Algolia 인덱스에서 온다. */
function AlgoliaSearchResult() {
  const { search } = useLocation();

  const [searchParams] = useSearchParams();

  const queryParameter = searchParams.get("q") ?? "";

  const queryString = useMemo(() => qs.parse(search), [search]);

  const { searchData, products, expandProducts, hasNextPage, loading, fetchMore } = useAlgoliaSearchResult(
    queryParameter,
    queryString as Record<string, unknown>,
  );

  return (
    <SearchResultView
      expandProducts={expandProducts}
      hasNextPage={hasNextPage}
      loading={loading}
      onFetchMore={fetchMore}
      products={products}
      queryParameter={queryParameter}
      searchData={searchData}
    />
  );
}

export default function SearchResult() {
  const [searchParams] = useSearchParams();
  const isAlgoliaDown = useIsAlgoliaDown();

  if (!ALGOLIA_ENABLED || isAlgoliaDown) return <ShopifySearchResult />;

  return (
    <AlgoliaProvider initialQuery={searchParams.get("q") ?? ""}>
      <AlgoliaSearchResult />
    </AlgoliaProvider>
  );
}
